<template>
  <div class="main-container">
    <v-card class="registration-card" elevation="2">
      <v-card-title class="registration-title">
        {{ isRegistered ? "You're In" : 'Registration' }}
      </v-card-title>
      <v-card-text>
        <!-- Pre-registration state -->
        <div v-if="!isRegistered">
          <p class="registration-subtitle mb-4">
            Welcome! To join the event, please register below.
          </p>

          <!-- Fees Display -->
          <div class="fees-section mb-4">
            <div class="fees-label">Fees</div>
            <div class="fees-amount">
              <span
                v-if="eventData.registrationType === 'Free'"
                class="free-text"
              >
                Free
              </span>
              <span v-else class="price-text">
                Rs. {{ eventData.registrationFees }}
              </span>
            </div>
          </div>

          <!-- Register Button -->
          <v-btn
            :href="eventData.registrationLink"
            target="_blank"
            rel="noopener noreferrer"
            class="register-btn w-100 mb-4"
            size="large"
            elevation="0"
            @click="handleRegistrationClick"
          >
            Register
          </v-btn>
        </div>

        <!-- Post-registration state -->
        <div v-else>
          <div class="mb-4 mt-4">
            <!-- Event Status Alert - unified for both virtual and in-person -->
            <div class="event-status mb-4">
              <v-alert
                :type="getEventAlertType()"
                variant="tonal"
                density="comfortable"
                :icon="getEventAlertIcon()"
                class="status-alert"
              >
                <div class="alert-content">
                  <div class="alert-title">{{ getEventTitle() }}</div>
                  <div class="alert-countdown" v-if="getEventCountdownDisplay()">
                    {{ getEventCountdownDisplay() }}
                  </div>
                </div>
              </v-alert>
            </div>

            <!-- Action buttons - unified layout for both event types -->
            <div class="action-buttons-row">
              <!-- Join Now button (for virtual) OR Get Directions button (for in-person) -->
              <v-btn
                v-if="isVirtualEvent"
                :href="isJoinButtonActive ? eventData.joinNowLink || '#' : '#'"
                :target="isJoinButtonActive ? '_blank' : '_self'"
                rel="noopener noreferrer"
                class="action-btn join-btn"
                size="large"
                elevation="0"
                variant="outlined"
                :disabled="!isJoinButtonActive"
                :class="{
                  'join-btn-active': isJoinButtonActive,
                  'join-btn-ended': isEventEnded,
                }"
              >
                <v-icon class="icon" size="small">
                  {{ getJoinButtonIcon() }}
                </v-icon>
                {{ getJoinButtonText() }}
              </v-btn>

              <v-btn
                v-else
                :href="getGoogleMapsUrl()"
                target="_blank"
                rel="noopener noreferrer"
                class="action-btn directions-btn"
                size="large"
                elevation="0"
                variant="outlined"
              >
                <v-icon class="direction-icon" size="small"
                  >mdi-directions</v-icon
                >
                Get Directions
              </v-btn>

              <!-- Add to Calendar button - same for both event types -->
              <v-btn
                @click="addToCalendar"
                class="action-btn calendar-btn"
                size="large"
                elevation="0"
                variant="flat"
              >
                <v-icon left size="small">mdi-calendar-plus-outline</v-icon>
                Add to calendar
              </v-btn>
            </div>

            <!-- Time until activation display - only for virtual events -->
            <!-- <div
              v-if="
                isVirtualEvent &&
                !isJoinButtonActive &&
                !isEventEnded &&
                timeUntilActivation
              "
              class="time-until-activation mt-3"
            >
              <small class="text-medium-emphasis">
                <v-icon size="small" class="mr-1">mdi-clock-outline</v-icon>
                Join button will activate before 30 minute of event starts
              </small>
            </div> -->
          </div>
        </div>
      </v-card-text>
    </v-card>

    <!-- Social Share Component -->
    <SocialShare />
  </div>
</template>

<script setup>
import { computed, onMounted, onUnmounted, ref } from 'vue';
import { useRoute } from 'vue-router';
import SocialShare from '../resources/partials/SocialShare.vue';

// Props
const props = defineProps({
  eventData: {
    type: Object,
    required: true,
    default: () => ({}),
  },
});

// Reactive data
const isRegistered = ref(false);
const currentTime = ref(new Date());
let timeUpdateInterval = null;

// LocalStorage key for this specific event
const getStorageKey = () => {
  return `event_registered_${
    props.eventData.id || props.eventData.name || 'default'
  }`;
};

// Check registration status from localStorage
const checkRegistrationFromStorage = () => {
  try {
    const storageKey = getStorageKey();
    const storedRegistration = localStorage.getItem(storageKey);
    return storedRegistration === 'true';
  } catch (error) {
    console.warn('LocalStorage not available:', error);
    return false;
  }
};

// Save registration status to localStorage
const saveRegistrationToStorage = () => {
  try {
    const storageKey = getStorageKey();
    localStorage.setItem(storageKey, 'true');
  } catch (error) {
    console.warn('Could not save to localStorage:', error);
  }
};

// Handle registration button click
const handleRegistrationClick = () => {
  // Save registration status to localStorage when user clicks register
  saveRegistrationToStorage();

  // Note: The actual registration happens via the external link
  // We assume if they clicked the button, they intend to register
  // You might want to add a delay or confirmation mechanism here
  setTimeout(() => {
    isRegistered.value = true;
  }, 1000); // Small delay to allow the registration page to open
};

// Check if user is registered based on URL parameters or localStorage
onMounted(() => {
  const route = useRoute();

  // First check URL parameters (existing logic)
  const hasSubmissionGuid = !!route.query.submissionGuid;

  // Then check localStorage
  const isStoredAsRegistered = checkRegistrationFromStorage();

  // User is considered registered if either condition is true
  isRegistered.value = hasSubmissionGuid || isStoredAsRegistered;

  // If URL has submissionGuid but localStorage doesn't have it, save it
  if (hasSubmissionGuid && !isStoredAsRegistered) {
    saveRegistrationToStorage();
  }

  // Update current time every minute for countdown and join button logic
  timeUpdateInterval = setInterval(() => {
    currentTime.value = new Date();
  }, 60000); // Update every minute
});

// Cleanup interval on unmount
onUnmounted(() => {
  if (timeUpdateInterval) {
    clearInterval(timeUpdateInterval);
  }
});

// Computed properties
const isVirtualEvent = computed(() => {
  return props.eventData.eventMode === 'Virtual';
});

const eventDate = computed(() => {
  if (!props.eventData.eventDate) return null;
  return new Date(parseInt(props.eventData.eventDate));
});

// Get event end time based on event mode - UNIFIED: both end 2 hours after start
const eventEndTime = computed(() => {
  if (!eventDate.value) return null;
  
  // Both Virtual and In-Person events end 2 hours after start time
  return new Date(eventDate.value.getTime() + 2 * 60 * 60 * 1000);
});

// Check if event has started
const hasEventStarted = computed(() => {
  if (!eventDate.value) return false;
  const now = currentTime.value;
  return now >= eventDate.value;
});

// Check if event has ended (unified logic for both virtual and in-person)
const isEventEnded = computed(() => {
  if (!eventEndTime.value) return false;
  const now = currentTime.value;
  return now > eventEndTime.value;
});

// Legacy computed property for backward compatibility (now uses unified logic)
const isMeetingEnded = computed(() => {
  return isEventEnded.value;
});

// Check if we're currently in the event window (event started but hasn't ended)
const isInEventWindow = computed(() => {
  return hasEventStarted.value && !isEventEnded.value;
});

// Legacy computed property for backward compatibility
const isInMeetingWindow = computed(() => {
  return isInEventWindow.value;
});

const eventCountdown = computed(() => {
  if (!eventDate.value) return '';

  const now = currentTime.value;

  if (isEventEnded.value) {
    return 'Over';
  }

  if (hasEventStarted.value) {
    // Show time remaining in event
    const timeRemaining = eventEndTime.value.getTime() - now.getTime();

    if (timeRemaining <= 0) {
      return 'Over';
    }

    const hours = Math.floor(timeRemaining / (1000 * 60 * 60));
    const minutes = Math.floor(
      (timeRemaining % (1000 * 60 * 60)) / (1000 * 60)
    );

    if (hours > 0) {
      return `${hours}h ${minutes}m remaining`;
    } else {
      return `${minutes}m remaining`;
    }
  }

  // Event hasn't started yet - show countdown to start
  const timeDiff = eventDate.value.getTime() - now.getTime();

  if (timeDiff <= 0) {
    return 'Event starting now';
  }

  const days = Math.floor(timeDiff / (1000 * 60 * 60 * 24));
  const hours = Math.floor(
    (timeDiff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)
  );

  if (days > 0) {
    return `${days}d ${hours}h`;
  } else if (hours > 0) {
    return `${hours}h`;
  } else {
    const minutes = Math.floor((timeDiff % (1000 * 60 * 60)) / (1000 * 60));
    return `${minutes}m`;
  }
});

// Updated join button activation logic (30 minutes before until event ends)
const isJoinButtonActive = computed(() => {
  if (!eventDate.value || !isVirtualEvent.value || !props.eventData.joinNowLink)
    return false;

  const now = currentTime.value;
  const eventTime = eventDate.value;
  const thirtyMinutesBefore = new Date(eventTime.getTime() - 30 * 60 * 1000);

  // Active from 30 minutes before until event ends
  return now >= thirtyMinutesBefore && now <= eventEndTime.value;
});

// Time until join button activation
const timeUntilActivation = computed(() => {
  if (
    !eventDate.value ||
    isJoinButtonActive.value ||
    !isVirtualEvent.value ||
    isEventEnded.value
  )
    return null;

  const now = currentTime.value;
  const eventTime = eventDate.value;
  const thirtyMinutesBefore = new Date(eventTime.getTime() - 30 * 60 * 1000);

  const timeDiff = thirtyMinutesBefore.getTime() - now.getTime();

  if (timeDiff <= 0) return null;

  const days = Math.floor(timeDiff / (1000 * 60 * 60 * 24));
  const hours = Math.floor(
    (timeDiff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)
  );
  const minutes = Math.floor((timeDiff % (1000 * 60 * 60)) / (1000 * 60));

  if (days > 0) {
    return `${days}d ${hours}h ${minutes}m`;
  } else if (hours > 0) {
    return `${hours}h ${minutes}m`;
  } else {
    return `${minutes}m`;
  }
});

// Methods to get dynamic labels and descriptions - UNIFIED for both event types
const getEventStatusLabel = () => {
  if (isEventEnded.value) return 'Event Status';
  if (isInEventWindow.value) return 'Event in Progress';
  return 'Event Starting in';
};

// Unified event alert methods for both virtual and in-person events
const getEventAlertType = () => {
  if (isEventEnded.value) return 'error';
  if (isInEventWindow.value) return 'success';
  return 'info';
};

const getEventAlertIcon = () => {
  if (isEventEnded.value) return 'mdi-clock-end';
  if (isInEventWindow.value) return 'mdi-play-circle';
  return 'mdi-clock-outline';
};

const getEventTitle = () => {
  if (isEventEnded.value) return 'Event has ended';
  if (isInEventWindow.value) return 'Event is in progress';
  return 'Event starting in';
};

const getEventCountdownDisplay = () => {
  if (!eventDate.value) return '';

  const now = currentTime.value;

  // No countdown after event ends
  if (isEventEnded.value) {
    return null;
  }

  if (hasEventStarted.value && !isEventEnded.value) {
    // Event is in progress - show negative countdown (time since event started)
    const timeSinceStart = now.getTime() - eventDate.value.getTime();
    const hours = Math.floor(timeSinceStart / (1000 * 60 * 60));
    const minutes = Math.floor(
      (timeSinceStart % (1000 * 60 * 60)) / (1000 * 60)
    );

    if (hours > 0) {
      return `-${hours}h ${minutes}m`;
    } else {
      return `-${minutes}m`;
    }
  }

  // Event hasn't started yet - show positive countdown to start
  const timeDiff = eventDate.value.getTime() - now.getTime();

  if (timeDiff <= 0) {
    return 'Starting now';
  }

  const days = Math.floor(timeDiff / (1000 * 60 * 60 * 24));
  const hours = Math.floor(
    (timeDiff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)
  );
  const minutes = Math.floor((timeDiff % (1000 * 60 * 60)) / (1000 * 60));

  if (days > 0) {
    return `${days}d ${hours}h ${minutes}m`;
  } else if (hours > 0) {
    return `${hours}h ${minutes}m`;
  } else {
    return `${minutes}m`;
  }
};

const getEventMessage = () => {
  // Return empty string to remove alert messages
  return '';
};

const getJoinButtonIcon = () => {
  if (isEventEnded.value) return 'mdi-video-off-outline';
  if (isJoinButtonActive.value) return 'mdi-video-outline';
  return 'mdi-video-off-outline';
};

const getJoinButtonText = () => {
  if (isEventEnded.value) return 'Join Now';
  if (isJoinButtonActive.value) return 'Join Now';
  return 'Join Now';
};

// Keep the legacy isEventSoon for backward compatibility if needed elsewhere
const isEventSoon = computed(() => {
  if (!eventDate.value) return false;
  const now = currentTime.value;
  const timeDiff = eventDate.value.getTime() - now.getTime();
  // Enable join button 30 minutes before event
  return timeDiff <= 30 * 60 * 1000 && timeDiff > 0;
});

// Methods
const getGoogleMapsUrl = () => {
  if (!props.eventData.location) return '#';
  const encodedLocation = encodeURIComponent(props.eventData.location);
  return `https://www.google.com/maps/dir/?api=1&destination=${encodedLocation}`;
};

const addToCalendar = () => {
  if (!eventDate.value) return;

  const startDate = eventDate.value;
  const endDate = eventEndTime.value; // Use the computed end time

  const formatDate = (date) => {
    return date.toISOString().replace(/[-:]/g, '').split('.')[0] + 'Z';
  };

  const title = encodeURIComponent(props.eventData.name || 'Event');

  // Enhanced description with meeting link
  let description = cleanDescription.value;
  if (isVirtualEvent.value && props.eventData.joinNowLink) {
    description +=
      (description ? '\n\n' : '') +
      `Join Meeting: ${props.eventData.joinNowLink}`;
  }
  const encodedDescription = encodeURIComponent(description);

  // Enhanced location handling
  let location = '';
  if (isVirtualEvent.value) {
    // For virtual events, use meeting link as location if available, otherwise use 'Virtual Event'
    location = props.eventData.joinNowLink || 'Virtual Event';
  } else {
    // For in-person events, use the physical location
    location = props.eventData.location || '';
  }
  const encodedLocation = encodeURIComponent(location);

  const startTime = formatDate(startDate);
  const endTime = formatDate(endDate);

  const googleCalendarUrl = `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${title}&dates=${startTime}/${endTime}&details=${encodedDescription}&location=${encodedLocation}`;

  window.open(googleCalendarUrl, '_blank');
};

const cleanDescription = computed(() => {
  if (!props.eventData.description) {
    return '';
  }

  // Handle if description is an array
  if (Array.isArray(props.eventData.description)) {
    return props.eventData.description.join(' ').replace(/<[^>]*>/g, '');
  }

  // Handle if description is a string
  if (typeof props.eventData.description === 'string') {
    return props.eventData.description.replace(/<[^>]*>/g, '');
  }

  return '';
});
</script>

<style scoped>
.main-container {
  max-width: 500px;
}
.registration-card {
  border-radius: 12px;
  overflow: hidden;
  background-color: #f8f9fa !important;
}

.registration-title {
  background-color: #f8f9fa;
  font-size: 1.25rem;
  font-weight: 600;
  text-align: left;
  padding: 1rem 1.5rem;
}

.registration-subtitle {
  color: #666;
  font-size: 0.9rem;
  margin-top: 1rem;
  text-align: left;
}

.fees-section {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 0;
  border-top: 1px solid #eee;
  border-bottom: 1px solid #eee;
}

.fees-label {
  font-weight: 600;
  color: #333;
}

.free-text {
  color: #28a745;
  font-weight: 600;
}

.price-text {
  color: #e74c3c;
  font-weight: 600;
}

.register-btn {
  background-color: #007ab2 !important;
  color: white !important;
  text-transform: none;
  border-radius: 8px;
}

.register-btn:hover {
  background-color: #00aeff !important;
}

.countdown-section-top {
  background-color: white;
  border-radius: 10px;
  padding: 1rem 0.5rem;
  text-align: left;
  margin-bottom: 1.5rem;
}

/* Updated countdown header with flex layout */
.countdown-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin: 1rem 0;
}

.countdown-icon-wrapper {
  display: flex;
  align-items: center;
  gap: 0.2rem;
}

.countdown-icon {
  color: #666;
  font-size: 1.2rem;
}

.countdown-label {
  color: #666;
  font-size: 0.9rem;
}

.countdown-time {
  font-size: 1.2rem;
  font-weight: 700;
  color: #e74c3c;
}

.countdown-description {
  color: #666;
  font-size: 0.85rem;
  line-height: 1.4;
  margin-bottom: 20px;
}

/* Event Status Alert - unified for both virtual and in-person */
.event-status {
  margin-bottom: 1.5rem;
}

.status-alert {
  border-radius: 10px !important;
}

.alert-content {
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  gap: 0.5rem;
}

.alert-title {
  font-weight: 600;
  font-size: 0.95rem;
  color: inherit;
}

.alert-countdown {
  font-size: 1.1rem;
  font-weight: 700;
  color: inherit;
}

.alert-message {
  font-size: 0.85rem;
  color: inherit;
  opacity: 0.9;
  line-height: 1.4;
}

/* Updated action buttons row layout - unified for both event types */
.action-buttons-row {
  display: flex;
  gap: 1rem;
  justify-content: space-between;
}

.action-btn {
  text-transform: none;
  border-radius: 8px;
  font-weight: 500;
  width: 160px;
  height: 35px;
  font-size: 14px;
}

/* Join Now and Get Directions - outlined style */
.join-btn,
.directions-btn {
  border: 1px solid #ccc !important;
  background-color: transparent !important;
  color: #666 !important;
}

/* Active join button styling */
.join-btn-active {
  border-color: #28a745 !important;
  color: #28a745 !important;
  background-color: #f8fff9 !important;
}

.join-btn-active:hover {
  background-color: #e8f5e8 !important;
  border-color: #1e7e34 !important;
}

/* Ended join button styling */
.join-btn-ended {
  border-color: #dc3545 !important;
  color: #dc3545 !important;
  background-color: #fff5f5 !important;
}

.direction-icon {
  margin-right: 5px;
}

.join-btn:hover:not(:disabled),
.directions-btn:hover {
  background-color: #f5f5f5 !important;
  border-color: #999 !important;
}

.join-btn:disabled {
  border-color: #e0e0e0 !important;
  color: #ccc !important;
}

/* Add to Calendar - white background (same for both event types) */
.calendar-btn {
  background-color: white !important;
  color: #666 !important;
  border: 1px solid #e0e0e0 !important;
}

.calendar-btn:hover {
  background-color: #f9f9f9 !important;
  border-color: #ccc !important;
}

/* Time until activation display - only shown for virtual events */
.time-until-activation {
  text-align: center;
  padding: 0.5rem;
  background-color: #f8f9fa;
  border-radius: 6px;
  border: 1px solid #e9ecef;
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .countdown-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.5rem;
  }

  .countdown-time {
    font-size: 1.5rem;
  }

  .action-buttons-row {
    flex-direction: column;
    width: 100%;
  }

  .action-btn {
    width: 100%;
  }
}

@media (max-width: 480px) {
  .countdown-header {
    flex-direction: column;
    align-items: flex-start;
  }

  .action-buttons-row {
    flex-direction: column;
    gap: 0.75rem;
  }
}
</style>